import type { ICurrency } from "@modules/website/model/domain/currency";
import type { IPreference, IPreferenceEditorResolve } from "@modules/partner/model/domain/preference";
import type { IMessageDialog } from "@qpUIs/qp-message-dialog/qp-message-dialog.interface";
import { appConfig } from "@env/app-config";
import type { ICurrencyObj } from "@modules/main/header/header";
import type { IWebsiteCurrencyRestService } from "@modules/website/rest/currency/website-currency-rest";
import { UserPreferences } from "@modules/basic/page/page-object-tree";
import type { IPreferenceEditorService } from "./preference-editor.service";
import { sendError } from "@modules/common/tool/request-fail/request-fail";

interface IPreferenceEditorBinding {
    modalInstance: angular.ui.bootstrap.IModalInstanceService;
    resolve: IPreferenceEditorResolve;
}
export class PreferenceEditorComponentController
    implements angular.IController, IPreferenceEditorBinding {
    public static readonly $inject = [
        "$scope",
        "$timeout",
        "websiteCurrencyRestService",
        "$translate",
        "qpMessageDialog",
        "preferenceEditorService",
        "$injector"
    ];
    /**
     * @desc 选择/切换的偏好, 在Editor内为 保存为 ICurrency, 在外部为 ICurrencyObj
     */
    public selectedPreferenceCurrency: ICurrency | null = null;
    public preferenceCurrencies: ICurrency[] | null = null;
    protected dropdownIsOpen: boolean = false;
    /**
     * @protected 获取下拉列表的状态
     */
    protected getDropdownListState = {
        inLoading: false,
        loading: "loading...",
        error: "",
    };
    /**
     * @protected 提交的状态
     */
    protected postApplyState = {
        /**
         * @desc not in use
         */
        inLoading: false,
        loading: "applying...",
        error: "",
    };
    /**
     * @private 请求下拉失败次数
     */
    private failGetDropDownListNumber: number = 0;
    /**
     * @private 请求提交失败次数
     */
    private failApplyNumber: number = 0;
    /**
     * @public 来自调用者注入的参数
     */
    public modalInstance: angular.ui.bootstrap.IModalInstanceService;
    /**
     * @public 来自调用者注入的参数
     */
    public resolve: IPreferenceEditorResolve;

    private cleanApplyTimer: angular.IPromise<void> | undefined = undefined;
    /**
     * @protected 只在 HTML 中使用
     */
    protected appName: string = appConfig.websiteCode;
    constructor(
        public $scope: angular.IScope,
        public $timeout: angular.ITimeoutService,
        public websiteCurrencyRestService: IWebsiteCurrencyRestService,
        public $translate: angular.translate.ITranslateService,
        public qpMessageDialog: IMessageDialog,
        public $injector: angular.auto.IInjectorService,
    ) { }

    public $onInit() {
        this.getDropDownList();
    }
    /**
     * @desc 确认选择
     * @protected 只在 HTML 中使用
     */
    protected ok() {
        // __DEV__:ok
        console.log(
            "ok",
            this.selectedPreferenceCurrency?.code,
            this.resolve.selectCurrencyCode
        );
        if (this.selectedPreferenceCurrency == null) {
            // 未选择, ui上不可能出现
            this.modalInstance.dismiss({ $reason: "never" });
        } else if (
            this.selectedPreferenceCurrency.code ===
            this.resolve.selectCurrencyCode
        ) {
            // 未更改的code, 也要fallback
            this.modalInstance.close(this.selectedPreferenceCurrency);
        } else {
            this.postApply();
        }
    }

    /**
     * @desc 取消选择
     * @protected 只在 HTML 中使用
     */
    protected cancel() {
        this.modalInstance.dismiss({ $reason: "cancel" });
    }
    /**
     * @desc 重试请求偏好列表
     * @protected 只在 HTML 中使用
     */

    protected retry($event: MouseEvent) {
        $event.stopPropagation();
        this.getDropDownList();
    }
    /**
     * @desc 获取下拉列表
     * @private 只在本类中使用
     */
    private getDropDownList() {
        this.getDropdownListState.inLoading = true;
        this.websiteCurrencyRestService
            .getAll()
            .then((res) => {
                this.getDropdownListState.error = "";
                if (!res || !res.success) {
                    throw res;
                }
                // 第一次未选择, fallback到默认值
                if (!this.selectedPreferenceCurrency) {
                    this.selectedPreferenceCurrency =
                        res.data.find(
                            (i) => this.resolve.selectCurrencyCode === i.code
                        ) ?? res.data[0];
                }
                this.$timeout(
                    () => {
                        this.preferenceCurrencies = res.data;
                    },
                    0,
                    false
                );
            })
            .catch((error) => {
                this.resolve.onRequestFailed(error).then((err) => {
                    this.failGetDropDownListNumber += 1;
                    // __DEV__:getList res
                    console.log("getDropDownList.error", err);
                    switch (String(err.code)) {
                        case "3000600":
                            this.getDropdownListState.error = err.message ?? "PlatformError";
                            break;
                        case "3000601":
                        case "3000602":
                        case "3000603":
                        case "3000604":
                        default:
                            // 未知错误;
                            this.getDropdownListState.error = err.message ?? "UnknownError";
                            break;
                    }
                    if (this.failGetDropDownListNumber > 5) {
                        // this.getDropdownListState.error = "Error many times, please try later";
                        this.qpMessageDialog
                            .info({ title: "Error", content: err.message ?? "NetworkError" })
                            .finally(() => {
                                this.modalInstance.dismiss({
                                    $reason: "fails", $message: err.message ?? "NetworkError"
                                });
                            });
                    }
                    sendError("PreferenceEditorComponentController.getDropDownList", err.message ?? "Error", { $injector: this.$injector });
                });
            })
            .finally(() => {
                this.$timeout(() => {
                    this.getDropdownListState.inLoading = false;
                }, 200);
            });
    }
    /**
     * @desc 提交选择偏好
     * @private 只在本类中使用
     */
    private postApply() {
        if (this.$timeout.cancel(this.cleanApplyTimer)) {
            // __DEV__:cleanApplyTimer
            console.log("cancel apply timer");
            this.postApplyState.error = "";
        }
        this.resolve.savePreference(<UserPreferences>{ currencyCode: this.selectedPreferenceCurrency!.code })
            .then((res) => {
                this.$timeout.cancel(this.cleanApplyTimer);
                // __DEV__:apply
                console.log("apply", res);
                if (res === true) {
                    this.modalInstance.close(this.selectedPreferenceCurrency);
                    this.postApplyState.error = "";
                    this.failApplyNumber = 0;
                } else {
                    throw res;
                }
            })
            .catch((error) => {
                this.resolve
                    .onRequestFailed(error)
                    .then((err) => {
                        // __DEV__:apply error
                        console.log("errorData", err);
                        this.failApplyNumber += 1;
                        switch (String(err.code)) {
                            case "3000600":
                                this.postApplyState.error = err.message ?? "PlatformError";
                                break;
                            case "3000601":/* 平台不支持当前货币 */
                            case "3000602":/* 平台没有货币设置 */
                            case "3000603":/* 平台没有货币配置 */
                            case "3000604":/* 货币不存在 */
                                this.postApplyState.error = err.message ?? "ParameterError";
                                break;
                            default:
                                // 未知错误;
                                this.postApplyState.error = err.message ?? "UnknownError";
                                break;

                        }
                        this.postApplyState.error += '.'.repeat(this.failApplyNumber)
                        this.cleanApplyTimer = this.$timeout(() => {
                            this.postApplyState.error = "";
                        }, 2000);
                        sendError("PreferenceEditorComponentController.postApply", error, { $injector: this.$injector });
                    })
            });
    }
}
